import { infiniteQueryOptions, keepPreviousData } from '@tanstack/react-query'

import { knockOutPublicFetcher } from '@/libs/fetcher/presets'

import { pickEventListRefreshInterval } from './eventListRefreshInterval'
import { navFilterToEventParams } from './navFilterToEventParams'
import type {
  PredictionContractGroup,
  PredictionEvent,
  PredictionEventsPage,
  PredictionEventsParams,
} from './types'

const PAGE_LIMIT = 20

/**
 * `game_status=scheduled,inprogress`，仅 sports lane（cdc-web CategoryPage
 * `CATEGORY_PAGE_GAME_STATUS` / og `DEFAULT_GAME_STATUS`）：砍掉已 final 的
 * 比赛。`game_status` 是 match 概念，predicts/crypto 这类无 match status 的
 * 类目附加它是语义污染，所以只对 asset_type 含 sports 的过滤生效。
 */
const SPORTS_GAME_STATUS = 'scheduled,inprogress'

export function predictionEventsInfiniteQueryOptions(
  params: PredictionEventsParams,
  pageLimit = PAGE_LIMIT,
) {
  // Live 节点（filter 含 "live":"true"）走专用端点 `/api/v1/events/live`
  // （cdc-web `useCategoryEvents` 的 EVENTS_LIVE_PUBLIC_PATH 同款）。
  //
  // 不能用 `/api/v1/events?live=true`：那条 query 实际是「在播**体育**比赛」
  // 路由，对 predicts 类目（crypto / financials）恒返回空——实测 prod
  // `?live=true&event_kind_groups=crypto` 0 条、`/events/live` 同过滤 30 条；
  // 连 All 视图也只给 sports（14 条 vs `/events/live` 的 8 sports + 22
  // predicts）。旧注释只发现「asset_type=predicts + live=true 查无数据」，
  // 用「不传 asset_type」绕开了 asset_type，但 event_kind_groups 组合同样被
  // 静默吞掉，于是左树点任一 predicts 子分类（Crypto/Financials）都是
  // "No events"。
  //
  // 端点切换后：live 键本身不再进 URL（`/events/live` 自带 live 语义，cdc-web
  // 同样只在私有端点才发 live 参数），asset_type 也一并不传（live 池同时含
  // sports 与 predicts）；live 树的 `event_kind_groups` 等组合键由
  // navFilterToEventParams 保留，子分类查询 = `/events/live + event_kind_groups`。
  const isLiveNode = params.navNode?.filter?.live === 'true'
  return infiniteQueryOptions({
    queryKey: [
      'prediction',
      'events',
      params.navNode?.id ?? 'all',
      params.sortBy,
      params.navNode?.filter ?? null,
      isLiveNode,
      pageLimit,
    ] as const,
    // 切 L1/L2/sort 时 queryKey 更换，新查询瞬时无 data——用 keepPreviousData
    // 让旧页卡片撑住画面（不闪白/不闪 empty），新数据到达后原位替换
    // （RQ v5 对 infinite query 的 placeholder 会在首次 fetch 后整页替换）。
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }): Promise<PredictionEventsPage> => {
      const filterParams = navFilterToEventParams(params.navNode?.filter ?? null)
      const queries: Record<string, string> = {
        ...(isLiveNode ? {} : { asset_type: 'predicts' }),
        status: 'active',
        limit: String(pageLimit),
        sort_by: params.sortBy,
        ...filterParams,
      }
      // sports lane 才附加 game_status（见 SPORTS_GAME_STATUS 注释）；判断基于
      // 过滤里实际生效的 asset_type，而非默认的 predicts。live 节点不加：
      // live 池是 sports + predicts 混合，game_status 是 match 概念，对其中
      // 的 predicts 是语义污染（cdc-web LivePage 同样不传）。
      if (filterParams.asset_type?.split(',').includes('sports')) {
        queries.game_status = SPORTS_GAME_STATUS
      }
      if (pageParam) queries.starting_after = pageParam
      // successResponseDataProperty: '' keeps the raw {has_more, data} body —
      // the fetcher's default `.data` unwrap would drop the pagination field
      // (repo pattern, see fetchCoins in services/coin/coins.ts).
      const res = await knockOutPublicFetcher<PredictionEventsPage>(
        isLiveNode ? '/api/v1/events/live' : '/api/v1/events',
        { queries },
        { successResponseDataProperty: '' },
      )
      return res ?? { has_more: false, data: [] }
    },
    initialPageParam: '',
    getNextPageParam: lastPage =>
      lastPage.has_more && lastPage.data.length > 0
        ? lastPage.data[lastPage.data.length - 1].id
        : undefined,
    // 自适应轮询（与 cdc-web 一致）：进行中赛事 5s、临开赛对齐唤醒、常规 30s、
    // 空列表 0（关闭）。RQ 回调拿 query.state.data（分页结构），flatMap 成平列表。
    refetchInterval: query =>
      pickEventListRefreshInterval(query.state.data?.pages.flatMap(p => p.data)),
  })
}

/**
 * 当前事件查询实际生效的 asset_type（默认 'predicts'，nav filter 可覆盖）——
 * 给 event kind groups 查询用（组列表按 asset_type 拉取）。
 *
 * live 节点仍是 'sports'：该查询只服务卡片页头图标/组名的**兜底链**
 * （`event.logo_url` 缺失时才查），而 live 池的 sports 侧（tennis 等）在
 * sports 组列表里；predicts 侧事件实测都自带 `logo_url` 与
 * `event_kind_display_name`，不走兜底。live 池本身是 sports + predicts 混合
 * （见 predictionEventsInfiniteQueryOptions 注释），一个 asset_type 无法覆盖
 * 两侧，取能覆盖兜底需求的一侧即可。
 */
export function predictionEventsAssetType(filter: Record<string, string> | null): string {
  if (filter?.live === 'true') return 'sports'
  return filter?.asset_type ?? 'predicts'
}

/**
 * 把 `/public/api/v1/contracts` 批量结果 merge 回事件（cdc-web
 * CategoryPage 同款）：league → `event.contracts` 顶层，match →
 * `event.sections`。enrichment 未返回的事件保持原样（卡片按无合约渲染）。
 */
export function enrichEventsWithContracts(
  events: PredictionEvent[],
  groups: PredictionContractGroup[] | undefined,
): PredictionEvent[] {
  if (!groups?.length) return events
  const byEventId = new Map(groups.map(g => [g.event_id, g]))
  return events.map(event => {
    const group = byEventId.get(event.id)
    if (!group) return event
    return { ...event, contracts: group.contracts, sections: group.sections ?? event.sections }
  })
}
